<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		DashLayout,
		CardGrid,
		DashWidgetContainer,
		DashWidgetGrid,
		Icon,
		Text,
		Button,
		DashActionArea,
		LabelV2,
	} from '@Shelly/shelly-design-system';
	import { edit } from 'src/stores/ui/edit_lists';
	import { longPressEditAvailable } from 'src/stores/features/dev/long-press-edit';
	import { buildListPressHoldOptions } from 'src/helpers/long_press_edit_helpers';
	import { routeParams } from 'src/stores/ui/viewport';
	import DashWidgetCard from 'src/components/Dashboards/DashWidgets/DashWidgetCard.svelte';
	import { dashboards } from 'src/stores/features/dashboards';
	import { sortBy } from '../list-helpers';
	import {
		getDashWidgetGridConfig,
		canAddDashboardWidget,
		isPredefinedDashboardId,
		mapDashItemsToDashWidgets,
		requestReorderDashboardWidget,
		staticCardGridConfig,
	} from 'src/components/Dashboards/dash-helpers';
	import DashWidgetMoreBtn from './Components/DashWidgetMoreBtn.svelte';
	import { onDestroy, onMount } from 'svelte';
	import DashWidgetActionModal from 'src/components/AddActions/Wizards/DashWidgetActionModal.svelte';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import type { DashboardRecord } from 'src/components/Dashboards/dash-types';
	import type { EnergyWidgetPreviewState } from 'src/components/EnergyMonitoring/v2/all-energy-widgets';
	import {
		isDashEnergyWidgetType,
		type DashEnergyWidgetConfig,
		type DashEnergyWidgetTypes,
	} from 'src/components/Dashboards/dash-energy-types';
	import { getEnergyWidgetPeriodLabel } from 'src/components/Dashboards/dash-energy-period';
	import { getEnergyConfigForType } from 'src/components/Dashboards/dash-energy-helpers';
	import {
		isDashPromoWidget,
		buildVirtualDashPromoWidgets,
	} from 'src/components/Dashboards/dash-promo-helpers';
	import {
		energyDashboardPromoSlotEnabled,
		customDashboardPromoSlotEnabled,
		dashPromoEmptyStateEnabled,
	} from 'src/stores/features/dev/dashboard-promo-slots';
	import {
		dashPromoActiveSampleIds,
		dashPromoDismissals,
		dashPromoMaxBanners,
	} from 'src/stores/features/dev/dashboard-promo-samples';
	import { verivoxDevForceEnable } from 'src/stores/features/dev/verivox-dev-force-enable';
	import { profile_data } from 'src/stores/account/user';
	import { ENERGY_DASHBOARD_ID } from 'src/components/EnergyMonitoring/v2/energy-v2-types';
	import ListSkeletonGrid from 'src/components/Lists/ListSkeletonGrid.svelte';
	import { forceListSkeleton } from 'src/stores/features/dev/force-list-skeleton';

	export let id;
	export let allowAddWidgets = true;
	export let allowManageWidgets = true;
	export let allowReorderWidgets = true;
	export let useEnergyPageContext = false;
	export let dashboardOverride: DashboardRecord | null | undefined = null;
	export let staticPreview = false;
	export let energyPreviewState: EnergyWidgetPreviewState = 'live';

	const flipDurationMs = 0;
	let dragDisabledLeft = true;
	let dragDisabledRight = true;
	let isMobile = false;
	let showAddWidget = false;
	let windowInnerWidth: number;
	let editColumn: 'left' | 'right' = 'left';
	let hideSelectDash = false;
	let previousDashboardId = id;
	let initialEnergyWidget = false;

	const energyMobileWidgetOrder: Partial<Record<DashEnergyWidgetTypes, number>> = {
		dashEnergyInfo: 0,
		dashEnergyKpiLive: 1,
		dashEnergyKpiPeriod: 2,
		dashEnergyGeneration: 3,
		dashEnergyFlow: 4,
		dashEnergyTopConsumers: 5,
		dashEnergyConsumerMix: 6,
		dashEnergyTariff: 7,
		dashEnergyInsights: 8,
		dashEnergyRoomDistribution: 9,
		dashEnergyRoomCharts: 10,
		dashEnergyPeak: 11,
		dashEnergyPhase: 12,
		dashEnergyDeviceTrend: 13,
		dashEnergyHeatmap: 14,
		dashEnergyPvDistribution: 15,
		dashEnergyEnvironmental: 16,
		dashEnergyRoomChart: 17,
	};

	const sortEnergyWidgetsForMobile = (items: any[]) =>
		items
			.map((item, sourceIndex) => ({ item, sourceIndex }))
			.sort((a, b) => {
				const aRank = isDashEnergyWidgetType(a.item?.type)
					? (energyMobileWidgetOrder[a.item.type] ?? Number.MAX_SAFE_INTEGER)
					: Number.MAX_SAFE_INTEGER;
				const bRank = isDashEnergyWidgetType(b.item?.type)
					? (energyMobileWidgetOrder[b.item.type] ?? Number.MAX_SAFE_INTEGER)
					: Number.MAX_SAFE_INTEGER;
				return aRank - bRank || a.sourceIndex - b.sourceIndex;
			})
			.map(({ item }) => item);

	$: isMobile = windowInnerWidth <= SystemScreenBreakpoints.MOBILE_MAX;
	$: isPredefinedDash = isPredefinedDashboardId(id);
	$: useAccessibleEnergyContainer =
		useEnergyPageContext && Number(id) === ENERGY_DASHBOARD_ID && !$edit;

	/**
	 * Virtual left-column BannersWidget for Energy / custom dashes.
	 * Gated by Dev slot toggles; Verivox inside requires de_users or force-enable.
	 */
	$: virtualPromoWidgets = (() => {
		void $energyDashboardPromoSlotEnabled;
		void $customDashboardPromoSlotEnabled;
		void $dashPromoEmptyStateEnabled;
		void $dashPromoActiveSampleIds;
		void $dashPromoDismissals;
		void $dashPromoMaxBanners;
		void $verivoxDevForceEnable;
		void $profile_data;
		if ($edit || staticPreview) return [];
		return buildVirtualDashPromoWidgets(id);
	})();

	$: showPromoWidget = virtualPromoWidgets.length > 0;
	$: promoWidget = virtualPromoWidgets[0] ?? null;

	const getWidgetGroupTitle = (item: { title?: string; showWidgetTitle?: boolean }) => {
		if (isPredefinedDash || item.showWidgetTitle !== false) return item.title ?? '';
		return '';
	};
	const getDisplayedEnergyConfig = (item: {
		type?: string;
		energyConfig?: Partial<DashEnergyWidgetConfig>;
	}): DashEnergyWidgetConfig | undefined =>
		isDashEnergyWidgetType(item.type)
			? getEnergyConfigForType(item.type, item.energyConfig)
			: undefined;
	const showsEnergyPeriodLabel = (item: {
		type?: string;
		energyConfig?: Partial<DashEnergyWidgetConfig>;
	}) => isDashEnergyWidgetType(item.type) && getDisplayedEnergyConfig(item)?.showPeriodLabel !== false;
	const getPeriodLabel = (item: { type?: string; energyConfig?: Partial<DashEnergyWidgetConfig> }) =>
		isDashEnergyWidgetType(item.type)
			? getEnergyWidgetPeriodLabel(
					item.type as DashEnergyWidgetTypes,
					getDisplayedEnergyConfig(item),
					$format,
				)
			: '';
	$: canAddWidget = allowAddWidgets && canAddDashboardWidget(id);

	$: currentDash = dashboardOverride ?? $dashboards[id];

	$: if (previousDashboardId !== id) {
		previousDashboardId = id;
		showAddWidget = false;
		edit.set(false);
	}

	$: allWidgets = currentDash
		? [...mapDashItemsToDashWidgets(currentDash, id)]
				.sort((x, y) => sortBy(x, y, 'position'))
				.filter((item) => item?.cards?.length > 0 || dashboardOverride != null)
		: [];
	$: energyMobileItems =
		useEnergyPageContext && isMobile && !$edit ? sortEnergyWidgetsForMobile(allWidgets) : null;

	/** Promo is rendered outside CardGrid — never in DnD lists. */
	$: dashItemsLeft = $edit
		? dashItemsLeft.filter((widget) => !isDashPromoWidget(widget))
		: (energyMobileItems ?? [...(allWidgets?.filter((item) => item?.col === 'left') || [])]);

	$: dashItemsRight = $edit
		? dashItemsRight
		: energyMobileItems
			? []
			: [...(allWidgets?.filter((item) => item?.col === 'right') || [])];

	$: hasColumnContent = allWidgets.length > 0 || showPromoWidget;

	//#region LEFT COLUMN CONTROL
	const handleReorderLeft = (e: CustomEvent) => {
		dashItemsLeft = e.detail.items;
	};

	const handleReorderLeftEnd = async (e: CustomEvent) => {
		dashItemsLeft = e.detail.items.map((item, i) => {
			return {
				...item,
				col: 'left',
				position: i,
			};
		});

		handleEndDragLeft();

		// Send request — never persist virtual promo widgets
		const allWidgets = [...dashItemsLeft, ...dashItemsRight].filter(
			(widget) => !isDashPromoWidget(widget),
		);
		let dashWidgets = {};

		allWidgets.map((v, i) => {
			delete v?.isDndShadowItem;
			dashWidgets[v.id] = {
				...v,
				cards: v.cards.map((item, y) => {
					return {
						id: item.id,
						_type: item._type,
						position: y,
					};
				}),
			};
		});

		await requestReorderDashboardWidget(id, dashWidgets);
	};

	const handleStartDragLeft = () => {
		dragDisabledLeft = false;
	};

	const handleEndDragLeft = () => {
		dragDisabledLeft = true;
	};
	//#regionend LEFT COLUMN CONTROL

	//#region RIGHT COLUMN CONTROL
	const handleReorderRight = (e: CustomEvent) => {
		dashItemsRight = e.detail.items;
	};

	const handleReorderRightEnd = async (e: CustomEvent) => {
		dashItemsRight = e.detail.items.map((item, i) => {
			return {
				...item,
				col: 'right',
				position: i,
			};
		});

		handleEndDragRight();

		// Send request — never persist virtual promo widgets
		const allWidgets = [...dashItemsLeft, ...dashItemsRight].filter(
			(widget) => !isDashPromoWidget(widget),
		);
		let dashWidgets = {};

		allWidgets.map((v, i) => {
			delete v?.isDndShadowItem;
			dashWidgets[v.id] = {
				...v,
				cards: v.cards.map((item, y) => {
					return {
						id: item.id,
						_type: item._type,
						position: y,
					};
				}),
			};
		});

		await requestReorderDashboardWidget(id, dashWidgets);
	};

	const handleStartDragRight = () => {
		dragDisabledRight = false;
	};

	const handleEndDragRight = () => {
		dragDisabledRight = true;
	};
	//#regionend RIGHT COLUMN CONTROL

	const getWidgetDndSettings = (items: any[], dragDisabled: boolean) => ({
		items,
		flipDurationMs,
		dragDisabled,
		// Supported by svelte-dnd-action: prevents a disabled drag zone from disabling its controls.
		autoAriaDisabled: true,
	});

	$: pressHoldLeft = buildListPressHoldOptions($edit, $longPressEditAvailable, {
		enabled: allowReorderWidgets && !staticPreview,
		onEntered: () => handleStartDragLeft(),
	});

	$: pressHoldRight = buildListPressHoldOptions($edit, $longPressEditAvailable, {
		enabled: allowReorderWidgets && !staticPreview,
		onEntered: () => handleStartDragRight(),
	});

	onDestroy(() => {
		edit.set(false);
	});

	onMount(() => {
		const openEnergyWidgetGallery = () => {
			initialEnergyWidget = true;
			hideSelectDash = true;
			showAddWidget = true;
		};
		window.addEventListener('shelly:open-energy-widget-gallery', openEnergyWidgetGallery);
		return () =>
			window.removeEventListener('shelly:open-energy-widget-gallery', openEnergyWidgetGallery);
	});
</script>

<svelte:window bind:innerWidth={windowInnerWidth} />

{#if $forceListSkeleton}
	<DashLayout
		class={useEnergyPageContext ? 'energy-page-dashboard' : ''}
		bind:isMobile
	>
		<svelte:fragment slot="leftContent">
			<ListSkeletonGrid
				count={8}
				variant="device-widget"
				{...staticCardGridConfig}
				gridGap={isMobile ? 'm' : 'l'}
			/>
		</svelte:fragment>
		<svelte:fragment slot="rightContent">
			{#if !isMobile}
				<ListSkeletonGrid count={8} variant="device-widget" {...staticCardGridConfig} />
			{/if}
		</svelte:fragment>
	</DashLayout>
{:else if !hasColumnContent}
	<div class="d-flex flex-direction-column align-items-center gap-m pt-md-l pt-sm-m">
		<Icon size="l" icon="fal fa-grid-2-plus" />
		<div class="d-flex flex-direction-column align-items-center gap-s">
			<Text class="text-center" element="h1">
				{$format('label_dash_empty_description', {
					default: 'Looks like your dashboard is empty.',
				})}
			</Text>
			<Text class="text-center text-color-muted">
				{$format('label_add_widget_prompt', { default: 'Add a widget to get started.' })}
			</Text>
		</div>
		<Button
			disabled={!canAddWidget}
			on:click={() => {
				showAddWidget = true;
				hideSelectDash = true;
			}}
		>
			{$format('button_add_widget', { default: 'Add widget' })}
		</Button>
	</div>
{:else}
	<DashLayout
		class={useEnergyPageContext ? 'energy-page-dashboard' : ''}
		bind:isMobile
		hideLeftContent={isMobile &&
			dashItemsLeft?.length === 0 &&
			!showPromoWidget &&
			!($edit && allowAddWidgets)}
		hideRightContent={isMobile && dashItemsRight?.length === 0}
	>
		<svelte:fragment slot="leftContent">
			{#if $edit && allowAddWidgets}
				<DashActionArea
					class={isMobile ? 'mb-m mt-l' : 'mb-m mt-m'}
					width="full"
					text={$format('button_add_widget', { default: 'Add widget' })}
					disabled={!canAddWidget}
					on:click={() => {
						if (!canAddWidget) return;
						if (isMobile) {
							editColumn = 'right';
						} else {
							editColumn = 'left';
						}
						showAddWidget = true;
					}}
				/>
			{/if}
			{#if showPromoWidget && promoWidget}
				<div class="dash-promo-slot dash-promo-slot-before pt-m {isMobile ? 'mb-m' : 'mb-l'}">
					<DashWidgetContainer
						title={$format('heading_promotions', { default: 'Promotions' })}
						showTitleRow
						editMode={false}
						role={useAccessibleEnergyContainer ? 'group' : 'button'}
						tabindex={useAccessibleEnergyContainer ? -1 : 0}
					>
						<DashWidgetGrid {...getDashWidgetGridConfig(promoWidget)}>
							<DashWidgetCard
								cards={promoWidget?.cards}
								type={promoWidget?.type}
								widgetId={String(promoWidget?.id ?? '')}
								title={promoWidget?.title}
								energyConfig={getDisplayedEnergyConfig(promoWidget)}
								promoBanners={promoWidget?.promoBanners ?? []}
								{useEnergyPageContext}
								{staticPreview}
								{energyPreviewState}
							/>
						</DashWidgetGrid>
					</DashWidgetContainer>
				</div>
			{/if}
			{#if dashItemsLeft.length > 0 || !(isMobile && $edit)}
				<CardGrid
					dndSettings={getWidgetDndSettings(dashItemsLeft, dragDisabledLeft)}
					role="list"
					aria-label={$format('label_dashboard_widgets_left', {
						default: 'Left dashboard widgets',
					})}
					editMode={$edit && allowReorderWidgets}
					noPaddingBottom={isMobile && dashItemsRight.length > 0}
					{...staticCardGridConfig}
					gridGap={isMobile ? 'm' : 'l'}
					{...isMobile ? { class: 'pb-0' } : {}}
					on:consider={handleReorderLeft}
					on:finalize={handleReorderLeftEnd}
				>
					{#each dashItemsLeft as item, i (item.id)}
						<div class:dnd-placeholder={item.isDndShadowItem} role="listitem">
							<DashWidgetContainer
								title={getWidgetGroupTitle(item)}
								showTitleRow={showsEnergyPeriodLabel(item)}
								editMode={$edit && allowReorderWidgets}
								role={useAccessibleEnergyContainer ? 'group' : 'button'}
								tabindex={useAccessibleEnergyContainer ? -1 : 0}
								pressHoldOptions={pressHoldLeft}
								on:handle-drag-start={handleStartDragLeft}
								on:handle-drag-end={handleEndDragLeft}
							>
								<svelte:fragment slot="titleRight">
									<div
										class="energy-widget-title-actions d-flex align-items-center gap-s"
									>
										{#if showsEnergyPeriodLabel(item)}
											<LabelV2 size="s" value={getPeriodLabel(item)} bgOpacity />
										{/if}
										{#if $edit && allowManageWidgets}
											<div
												class="d-flex"
												role="presentation"
												data-dnd-ignore
												on:pointerdown={(e) => e.stopPropagation()}
												on:mousedown={(e) => e.stopPropagation()}
												on:touchstart={(e) => e.stopPropagation()}
												on:click={(e) => e.stopPropagation()}
											>
												<DashWidgetMoreBtn
													dashboardId={id}
													editItem={item}
													energyOnly={useEnergyPageContext}
													on:delete-widget-finish={(e) => {
														const deleteWidgetID = e?.detail?.id;
														dashItemsLeft = dashItemsLeft.filter(
															(dashWidget) =>
																dashWidget.id !== deleteWidgetID,
														);
													}}
												/>
											</div>
										{/if}
									</div>
								</svelte:fragment>

								<DashWidgetGrid {...getDashWidgetGridConfig(item)}>
									<DashWidgetCard
										cards={item?.cards}
										type={item?.type}
										widgetId={String(item?.id ?? '')}
										title={item?.title}
										cameraConfig={item?.cameraConfig}
										deviceCardConfig={item?.deviceCardConfig}
										energyConfig={getDisplayedEnergyConfig(item)}
										promoBanners={item?.promoBanners ?? []}
										widgetItem={item}
										{useEnergyPageContext}
										{staticPreview}
										{energyPreviewState}
									/>
								</DashWidgetGrid>
							</DashWidgetContainer>
						</div>
					{/each}
				</CardGrid>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="rightContent">
			{#if $edit && !isMobile && allowAddWidgets}
				<DashActionArea
					class="mb-m mt-m"
					width="full"
					text={$format('button_add_widget', { default: 'Add widget' })}
					disabled={!canAddWidget}
					on:click={() => {
						if (!canAddWidget) return;
						editColumn = 'right';
						showAddWidget = true;
					}}
				/>
			{/if}
			<CardGrid
				dndSettings={getWidgetDndSettings(dashItemsRight, dragDisabledRight)}
				role="list"
				aria-label={$format('label_dashboard_widgets_right', {
					default: 'Right dashboard widgets',
				})}
				editMode={$edit && allowReorderWidgets}
				{...staticCardGridConfig}
				gridGap={isMobile ? 'm' : 'l'}
				{...isMobile ? { class: 'pb-0' } : {}}
				on:consider={handleReorderRight}
				on:finalize={handleReorderRightEnd}
			>
				{#each dashItemsRight as item, i (item.id)}
					<div class:dnd-placeholder={item.isDndShadowItem} role="listitem">
						<DashWidgetContainer
							title={getWidgetGroupTitle(item)}
							showTitleRow={showsEnergyPeriodLabel(item)}
							editMode={$edit && allowReorderWidgets}
							role={useAccessibleEnergyContainer ? 'group' : 'button'}
							tabindex={useAccessibleEnergyContainer ? -1 : 0}
							pressHoldOptions={pressHoldRight}
							on:handle-drag-start={handleStartDragRight}
							on:handle-drag-end={handleEndDragRight}
						>
							<svelte:fragment slot="titleRight">
								<div class="energy-widget-title-actions d-flex align-items-center gap-s">
									{#if showsEnergyPeriodLabel(item)}
										<LabelV2 size="s" value={getPeriodLabel(item)} bgOpacity />
									{/if}
									{#if $edit && allowManageWidgets}
										<div
											class="d-flex"
											role="presentation"
											data-dnd-ignore
											on:pointerdown={(e) => e.stopPropagation()}
											on:mousedown={(e) => e.stopPropagation()}
											on:touchstart={(e) => e.stopPropagation()}
											on:click={(e) => e.stopPropagation()}
										>
											<DashWidgetMoreBtn
												dashboardId={id}
												editItem={item}
												energyOnly={useEnergyPageContext}
												on:delete-widget-finish={(e) => {
													const deleteWidgetID = e?.detail?.id;
													dashItemsRight = dashItemsRight.filter(
														(dashWidget) => dashWidget.id !== deleteWidgetID,
													);
												}}
											/>
										</div>
									{/if}
								</div>
							</svelte:fragment>

							<DashWidgetGrid {...getDashWidgetGridConfig(item)}>
								<DashWidgetCard
									cards={item?.cards}
									type={item?.type}
									widgetId={String(item?.id ?? '')}
									title={item?.title}
									cameraConfig={item?.cameraConfig}
									deviceCardConfig={item?.deviceCardConfig}
									energyConfig={getDisplayedEnergyConfig(item)}
									widgetItem={item}
									{useEnergyPageContext}
									{staticPreview}
									{energyPreviewState}
								/>
							</DashWidgetGrid>
						</DashWidgetContainer>
					</div>
				{/each}
			</CardGrid>
		</svelte:fragment>
	</DashLayout>
{/if}

<DashWidgetActionModal
	hideSelectDash={hideSelectDash || isPredefinedDash}
	dashboardId={id}
	routeParams={$routeParams}
	isAddWizard={true}
	dashEditItem={undefined}
	bind:showModal={showAddWidget}
	bind:editColumn
	{initialEnergyWidget}
	energyOnly={useEnergyPageContext}
	on:close={() => {
		hideSelectDash = false;
		initialEnergyWidget = false;
	}}
/>

<style>
	/* Period metadata stays at the trailing edge even when the optional section title is hidden. */
	:global(.dash-widget-title-row) .energy-widget-title-actions,
	:global(.dash-widget-title-container) .energy-widget-title-actions {
		margin-left: auto;
	}

	/*
	 * Promo / Add-widget sit above CardGrid as left-column chrome (outside DnD).
	 * DashLayout puts padding-top on `.card-grid-wrapper`; with a sibling above
	 * that wrapper the inset sat *between* the chrome and the first widget, and
	 * the chrome sat flush under the tabs. Move the inset onto the chrome instead
	 * (`pt-m` / `mt-s`/`mt-m` on the slot or action area) and zero the wrapper inset.
	 */
	:global(.dash-promo-slot) {
		flex: 0 0 auto;
	}

	:global(
		.dashboard-layout
			.dash-column:has(> .dash-promo-slot-before, > .dash-action-area)
			> .card-grid-wrapper
	) {
		padding-top: 0;
	}

	/* Production Energy dashboard readability and bottom-dock clearance. */
	:global(.energy-page-dashboard .miniature-styles) {
		font-size: 12px !important;
		line-height: 16px !important;
	}

	:global(.energy-page-dashboard .energy-widget-title-actions) {
		font-size: 12px;
	}

	@media (max-width: 639px) {
		:global(.energy-page-dashboard .dashboard-scroll) {
			padding-bottom: calc(
				var(--floating-dock-scroll-clearance, 72px) + var(--spacing-l, 24px)
			) !important;
		}
	}
</style>
